/**
 * Who Icons — GA4 event tracking + Meta custom events
 * Hooks into existing custom events dispatched by product-form.js and global.js
 *
 * Meta standard events (PageView, ViewContent, AddToCart, InitiateCheckout,
 * Search, Purchase) are sent by the Facebook & Instagram app (Pixel + CAPI).
 * Do NOT call fbq() here — the app exposes a global fbq, so any call would
 * double-count. Events the app doesn't cover (Lead, CompleteRegistration) are
 * published as Shopify custom events and sent by the "Meta – Lead &
 * Registration" custom pixel (Settings > Customer events).
 */

window.dataLayer = window.dataLayer || [];

// ---------------------------------------------------------------------------
// ViewContent / view_item
// Data is output by jkl-main-product.liquid as <script type="application/json">
// ---------------------------------------------------------------------------

(function () {
  var el = document.getElementById('tracking-product-data');
  if (!el) return;
  try {
    var d = JSON.parse(el.textContent);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: d.currency,
        value: d.price,
        items: [{
          item_id: String(d.item_id),
          item_name: d.item_name,
          item_brand: d.item_brand,
          item_category: d.item_category,
          item_variant: d.item_variant,
          price: d.price,
          quantity: 1,
        }],
      },
    });
  } catch (e) {}
})();

// ---------------------------------------------------------------------------
// generate_lead / Lead
// Newsletter success <h3> has data-tracking-lead set by Liquid
// (jkl-footer.liquid, newsletter.liquid)
// ---------------------------------------------------------------------------

(function () {
  var el = document.querySelector('[data-tracking-lead]');
  if (!el) return;
  var method = el.dataset.trackingLead || 'newsletter';
  window.dataLayer.push({ event: 'generate_lead', method: method });
  publishCustomEvent('wi_lead', { method: method });
})();

// ---------------------------------------------------------------------------
// sign_up / CompleteRegistration
// main-register.liquid sets a sessionStorage flag on submit. Shopify logs the
// customer in after a successful registration, so the flag + a logged-in
// customer on the next page load means the registration succeeded.
// ---------------------------------------------------------------------------

(function () {
  var flag;
  try {
    flag = sessionStorage.getItem('wi_registration_pending');
    if (flag) sessionStorage.removeItem('wi_registration_pending');
  } catch (e) {}
  if (!flag) return;
  if (!(window.WI_TRACKING && window.WI_TRACKING.customerLoggedIn)) return;
  window.dataLayer.push({ event: 'sign_up', method: 'email' });
  publishCustomEvent('wi_complete_registration', { method: 'email' });
})();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pushGA4(eventName, params) {
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(Object.assign({ event: eventName }, params));
}

// Shopify custom event -> picked up by custom pixels via analytics.subscribe()
function publishCustomEvent(eventName, data) {
  function publish() {
    var analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;
    analytics.publish(eventName, data || {});
    return true;
  }
  if (!publish()) window.addEventListener('load', publish, { once: true });
}

function formatPrice(cents) {
  return (cents / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// AddToCart — listens to variant:add (product-form.js line 79)
// Then waits for cart:change to get full item data
// ---------------------------------------------------------------------------

let pendingAdd = null;

document.addEventListener('variant:add', function (e) {
  pendingAdd = {
    variantId: String(e.detail.variant.id),
    quantity: e.detail.quantity || 1,
  };
});

document.addEventListener('cart:change', function (e) {
  if (!pendingAdd) return;

  const cart = e.detail.cart;
  const item = cart.items.find(function (i) {
    return String(i.variant_id) === pendingAdd.variantId;
  });

  if (!item) {
    pendingAdd = null;
    return;
  }

  const ga4Item = {
    item_id: item.sku || String(item.variant_id),
    item_name: item.product_title,
    item_variant: item.variant_title !== 'Default Title' ? item.variant_title : undefined,
    item_brand: item.vendor,
    price: parseFloat(formatPrice(item.price)),
    quantity: pendingAdd.quantity,
  };

  pushGA4('add_to_cart', {
    ecommerce: {
      currency: cart.currency,
      value: parseFloat(formatPrice(item.price * pendingAdd.quantity)),
      items: [ga4Item],
    },
  });

  pendingAdd = null;
});

// ---------------------------------------------------------------------------
// InitiateCheckout / begin_checkout
// Fires on both cart drawer (#CartDrawer-Checkout) and cart page checkout button
// ---------------------------------------------------------------------------

document.addEventListener('click', function (e) {
  const btn = e.target.closest('[name="checkout"]');
  if (!btn) return;

  fetch('/cart.js')
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      if (!cart.items || !cart.items.length) return;

      pushGA4('begin_checkout', {
        ecommerce: {
          currency: cart.currency,
          value: parseFloat(formatPrice(cart.total_price)),
          items: cart.items.map(function (item) {
            return {
              item_id: item.sku || String(item.variant_id),
              item_name: item.product_title,
              item_variant: item.variant_title !== 'Default Title' ? item.variant_title : undefined,
              item_brand: item.vendor,
              price: parseFloat(formatPrice(item.price)),
              quantity: item.quantity,
            };
          }),
        },
      });
    });
});

// ---------------------------------------------------------------------------
// Search — fires on search form submit OR input change (predictive search, debounced)
// ---------------------------------------------------------------------------

var lastSearchTerm = '';
var searchTimeout;

function trackSearch(term) {
  term = (term || '').trim();
  if (!term || term === lastSearchTerm) return;
  lastSearchTerm = term;
  console.log('[Tracking] Search:', term);
  pushGA4('search', { search_term: term });
}

// Form submit (traditional search)
document.addEventListener('submit', function (e) {
  const form = e.target.closest('form[action*="/search"]');
  if (!form) return;
  const input = form.querySelector('input[type="search"], input[name="q"]');
  if (input) {
    console.log('[Tracking] Search form submitted:', input.value);
    trackSearch(input.value);
  }
});

// Predictive search input — debounced (only after user stops typing for 500ms)
document.addEventListener('input', function (e) {
  if (!e.target.matches('input[type="search"]')) return;
  if (e.target.value.length < 2) return;
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(function () {
    trackSearch(e.target.value);
  }, 500);
}, true);

// ---------------------------------------------------------------------------
// Scroll depth — 25 / 50 / 75 / 100%
// ---------------------------------------------------------------------------

(function () {
  const thresholds = [25, 50, 75, 100];
  const fired = {};

  function getScrollPercent() {
    const el = document.documentElement;
    const scrollTop = window.scrollY || el.scrollTop;
    const scrollHeight = el.scrollHeight - el.clientHeight;
    if (scrollHeight <= 0) return 100;
    return Math.round((scrollTop / scrollHeight) * 100);
  }

  function onScroll() {
    const pct = getScrollPercent();
    thresholds.forEach(function (t) {
      if (!fired[t] && pct >= t) {
        fired[t] = true;
        pushGA4('scroll', { percent_scrolled: t });
      }
    });
    if (fired[100]) {
      window.removeEventListener('scroll', onScroll, { passive: true });
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
})();
